import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { SocketContext } from "../../../socket";
import LotteryListRenderer from "./LotteryListRenderer";

const LotteryListGetter: React.FC<{}> = () => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const { t } = useTranslation("translation");
  const navigate = useNavigate();
  // const socket = React.useContext(SocketContext);
  const [data, setData] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [socket, setSocket] = React.useState<any>();

  // const socket = io(apiBaseURL, {
  //   autoConnect: false,
  //   query: {
  //     userId: JSON.parse(localStorage.getItem("UserData") || "")?.uId,
  //   },
  // });

  let localData = localStorage?.getItem("UserData");
  if(!localData) {
    localStorage.clear();
    navigate("/login")
  }
  const auth = localData ? JSON.parse(localStorage.getItem("UserData") || "") : null;

  // The screen to display. "?screen=N" wins over the stored choice so that several
  // screen tabs can be open at once: localStorage is shared across tabs, so the last
  // one opened used to redefine what every other tab thought it was showing.
  const [searchParams] = useSearchParams();
  const screenParam = searchParams.get("screen");
  const screen = localStorage.getItem("screenNumber");
  const storedScreenNumber = screen
    ? JSON.parse(localStorage.getItem("screenNumber") || "")
    : null;
  const screenNumberDetails = screenParam ? parseInt(screenParam) : storedScreenNumber;

  // Settings for the screen named in the URL are fetched here and handed to the
  // renderer, instead of letting it read the shared localStorage copy.
  const [urlScreenSettings, setUrlScreenSettings] = React.useState<any>(null);
  const [urlScreenOneSettings, setUrlScreenOneSettings] = React.useState<any>(null);
  // The display sizes its ticket grid ONCE, in a mount effect, from settings.rows /
  // settings.column. So it must not mount before those settings exist: a first paint
  // with stale or missing settings locks in the wrong rows and columns for good, and
  // the tickets end up cropped. Only relevant for the "?screen=" entry point, which
  // loads its own settings; without the parameter the stored ones are already there.
  const [screenSettingsReady, setScreenSettingsReady] = React.useState<boolean>(!screenParam);

  React.useEffect(() => {
    if (!screenParam || !auth?.uId) {
      setScreenSettingsReady(true);
      return;
    }
    axios
      .get(`${apiBaseURL}/api/settings/fetchSettings?id=${auth?.uId}&screenNumber=${screenNumberDetails}`)
      .then((res: any) => {
        const detail = res?.data?.settings;
        if (!detail) return;
        setUrlScreenSettings({
          settingId: detail?.id,
          screenNumber: detail?.screenNumber,
          headerFooterStyle: detail?.headerFooterStyle,
          ticketsCount: detail?.ticketsCount,
          rows: detail?.rows,
          column: detail?.column,
          emptyTicketsDisplay: detail?.emptyTicketsDisplay,
          backgroundType: detail?.backgroundType,
          backgroundValue: detail?.backgroundValue,
          orientation: detail?.orientation ?? "LANDSCAPE",
        });
        if (res?.data?.screenOneSettings) {
          setUrlScreenOneSettings({ ticketsCount: res.data.screenOneSettings.ticketsCount });
        }
      })
      .catch((err: any) => console.log("screen settings err", err))
      // released on failure too, so a settings outage shows the old behaviour
      // rather than an empty screen that never renders
      .finally(() => setScreenSettingsReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenParam]);

  React.useEffect(() => {
    setLoading(true);
    // socket.connect();
    const establishedEvent = io(apiBaseURL, {
      autoConnect: true,
      query: {
        userId: JSON.parse(localStorage.getItem("UserData") || "")?.uId,
      },
    });
    setSocket(establishedEvent);

    //Update screen number with dynamic data, once you get settings API
    axios
      .get(
        `${apiBaseURL}/api/ticket/fetchShopBasedLinkedTickets?shopId=${auth?.uId}&screenNumber=${screenNumberDetails}`,
      )
      .then((res: any) => {
        const updatedData: any = [];
        res?.data?.linkedTickets?.map((data: any, index: any) => {
          updatedData[data?.ticketPosition] = data;
        });
        setData(updatedData);
      })
      .catch((error: Error) => {
        console.log("err", error);
      })
      .finally(() => setLoading(false));

    return () => {
      socket && socket.disconnect();
    };
  }, []);

  React.useEffect(() => {
    socket && socket.once("fetchTicketByNumberResponse", (response: any) => {
      if (response?.message || response?.error) {
        return null;
      } else {
        const updatedData = [...data];
        if(screenNumberDetails == response?.screenNumber) {
          updatedData[response.ticketPosition] = {
            ...response.ticket,
            ...{ linkedId: response?.linkedId, screenNumber: response?.screenNumber },
          };
        }
        setData(updatedData);
      }
    });

    socket && socket.once("deleteLinkedTicketResponse", (response: any) => {
      const updatedData = [...data];
      if(screenNumberDetails == response?.screenNumber) {
        updatedData[response.ticketPosition] = {}
      }
      setData(updatedData);
    });

    socket && socket.once("updateLinkedTicketResponse", (response: any) => {
      const updatedData = [...data];
      if(screenNumberDetails == response?.screenNumber) {
        updatedData[response.ticketPosition] = {
          ...updatedData[response.ticketPosition],
          isAnimate: response.ticket?.[0].isAnimate ?? updatedData[response.ticketPosition].isAnimate,
          isNew: response.ticket?.[0].isNew ?? updatedData[response.ticketPosition].isNew,
          isSoldOut: response.ticket?.[0].isSoldOut ?? updatedData[response.ticketPosition].isSoldOut,
          isWinner: response.ticket?.[0].isWinner ?? updatedData[response.ticketPosition].isWinner,
          prizeMoney: response.ticket?.[0].prizeMoney ?? updatedData[response.ticketPosition].prizeMoney
        };
      }
      setData(updatedData);
    });

    return () => {
      socket && socket.off("fetchTicketByNumberResponse");
      socket && socket.off("deleteLinkedTicketResponse");
      socket && socket.off("updateLinkedTicketResponse");
    };
  }, [socket, data]);

  // hold the display back until this screen's settings are in hand (see above)
  if (!screenSettingsReady) return null;

  return (
    <LotteryListRenderer
      data={data}
      loading={loading}
      count={totalCount}
      settingsOverride={urlScreenSettings}
      screenOneSettingsOverride={urlScreenOneSettings}
    />
  );
};
export default LotteryListGetter;
